// src/services/looker/lookerLayout.ts
/**
 * Where each Looker tile sits on /looker for one workspace, and how wide it is.
 * Pure: the template supplies the tiles, a saved layout (if any) supplies the
 * order and widths, and the two are merged so a template edit never strands a
 * workspace — tiles it removed drop out, tiles it added join right after the template tile that precedes them.
 *
 * @module services/looker/lookerLayout
 */
import { z } from 'zod';
import type { LookerTile, LookerTileWidth } from './lookerTypes';

export type { LookerTileWidth } from './lookerTypes';
/** The widths a tile cycles through when arranging: 2, 4, 6, 12 of 12 columns. */
export const LOOKER_TILE_WIDTHS: readonly LookerTileWidth[] = ['sixth', 'third', 'half', 'full'];
/** `sixth` is for single values only: a chart or table that small cannot be read. */
export const allowsSixth = (tile: Pick<LookerTile, 'type'>): boolean => tile.type === 'single_value';
/** The width a tile is actually drawn at: a `sixth` on any other type falls back to `third`. */
export const fitWidth = (w: LookerTileWidth, tile: Pick<LookerTile, 'type'>): LookerTileWidth =>
  w === 'sixth' && !allowsSixth(tile) ? 'third' : w;
/** The next width in the cycle, skipping `sixth` unless the tile may have it. */
export const nextWidth = (w: LookerTileWidth, allowSixth: boolean): LookerTileWidth => {
  const cycle = LOOKER_TILE_WIDTHS.filter((x) => allowSixth || x !== 'sixth');
  const i = cycle.indexOf(!allowSixth && w === 'sixth' ? 'third' : w);
  return cycle[(i + 1) % cycle.length];
};

export interface LookerLayoutEntry {
  id: string;
  width: LookerTileWidth;
}

/** Generous: a template with more tiles than this is not a dashboard anyone reads. */
export const LOOKER_LAYOUT_MAX_TILES = 200;

const LayoutSchema = z
  .array(z.object({ id: z.string().min(1).max(64), width: z.enum(['sixth', 'third', 'half', 'full']) }))
  .max(LOOKER_LAYOUT_MAX_TILES);

/** A stored layout, or null when it is missing or malformed — the template order then applies. */
export const parseLayout = (raw: unknown): LookerLayoutEntry[] | null => {
  const parsed = LayoutSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
};

/** The template's own order and widths; a tile with no width is full where its author made it wide. */
export const defaultLayout = (tiles: readonly LookerTile[]): LookerLayoutEntry[] =>
  tiles.map((t) => ({ id: t.id, width: fitWidth(t.width ?? (t.wide ? 'full' : 'half'), t) }));

export const resolveLayout = (
  tiles: readonly LookerTile[],
  saved: readonly LookerLayoutEntry[] | null,
): LookerLayoutEntry[] => {
  if (!saved) return defaultLayout(tiles);
  const byId = new Map(tiles.map((t) => [t.id, t]));
  const seen = new Set<string>();
  const kept = saved.flatMap((e): LookerLayoutEntry[] => {
    const tile = byId.get(e.id);
    if (!tile || seen.has(e.id)) return [];
    seen.add(e.id);
    return [{ ...e, width: fitWidth(e.width, tile) }];
  });
  return placeNew(kept, defaultLayout(tiles), seen);
};

/**
 * Each tile the saved layout lacks goes right after its nearest preceding
 * template tile already placed (new ones chain in template order), else first.
 */
const placeNew = (
  kept: readonly LookerLayoutEntry[],
  template: readonly LookerLayoutEntry[],
  seen: ReadonlySet<string>,
): LookerLayoutEntry[] => {
  const result = template.reduce<{ out: LookerLayoutEntry[]; prev: string | null }>(
    (acc, e) => {
      if (seen.has(e.id)) return { out: acc.out, prev: e.id };
      const at = acc.prev === null ? 0 : acc.out.findIndex((x) => x.id === acc.prev) + 1;
      return { out: [...acc.out.slice(0, at), e, ...acc.out.slice(at)], prev: e.id };
    },
    { out: [...kept], prev: null },
  );
  return result.out;
};

export const moveEntry = (
  layout: readonly LookerLayoutEntry[],
  from: number,
  to: number,
): LookerLayoutEntry[] => {
  const inRange = (i: number) => i >= 0 && i < layout.length;
  if (!inRange(from) || !inRange(to) || from === to) return [...layout];
  const rest = layout.filter((_, i) => i !== from);
  return [...rest.slice(0, to), layout[from], ...rest.slice(to)];
};

export const setWidth = (
  layout: readonly LookerLayoutEntry[],
  id: string,
  width: LookerTileWidth,
): LookerLayoutEntry[] => layout.map((e) => (e.id === id ? { ...e, width } : e));
